import React from 'react';
import PropTypes from 'prop-types';
import uuid from 'uuid/v4';
import {List} from 'immutable';
import classnames from 'classnames';
import {callTargetedAction} from 'client/framework';
import makeRef from 'lib/makeRef';
import Tooltip from 'sg/Tooltip/Tooltip.react';
import tooltipActions from 'sg/Tooltip/Tooltip.actions';
import './sg-truncate-tooltip.scss';

export default class TruncateTooltip extends React.Component {
  static propTypes = {
    className: PropTypes.string,
    color: PropTypes.oneOf(['light', 'dark']),
    content: PropTypes.oneOfType([PropTypes.instanceOf(List), PropTypes.string]).isRequired,
    position: PropTypes.oneOf(['top', 'right', 'bottom', 'left']),
    storeName: PropTypes.string,
    onHover: PropTypes.bool,
    tooltipClassName: PropTypes.string
  };

  static defaultProps = {
    color: 'dark',
    position: 'right',
    onHover: false
  };

  constructor(props) {
    super(props);
    this.storeName = props.storeName || `TruncateTooltipStore${uuid()}`;
  }

  componentDidMount() {
    this.updatePosition();
    global.addEventListener('scroll', this.optimizedUpdatePosition, true);
  }

  componentWillUnmount() {
    global.removeEventListener('scroll', this.optimizedUpdatePosition);
  }

  makeRef = makeRef.bind(this);

  optimizedUpdatePosition = () => {
    global.requestAnimationFrame(this.updatePosition);
  };

  updatePosition = () => {
    const wordRect = this.truncateWordRef.getBoundingClientRect();
    this.tooltipRef.style.top = `${wordRect.top + wordRect.height}px`;
    this.tooltipRef.style.left = `${wordRect.width / 2 + wordRect.left}px`;
  };

  toggleDropdown = (e) => {
    if (
      (!List.isList(this.props.content) && e.target.clientWidth >= e.target.scrollWidth) ||
      (List.isList(this.props.content) && this.props.content.size <= 1)
    ) {
      return;
    }

    this.updatePosition();

    callTargetedAction({
      name: tooltipActions.TOGGLE_VISIBILITY,
      targetStore: this.storeName
    });
  };

  noop = () => {};

  makeListContent() {
    const truncateList = this.props.content;
    const additionalCount =
      truncateList.size - 1 > 0 ? (
        <span className='sg-truncate-tooltip__additional-count'>+{truncateList.size - 1} more</span>
      ) : null;
    const listClasses = classnames('sg-truncate-tooltip__list', {
      'sg-truncate-tooltip__cursor': additionalCount
    });

    return (
      <div
        onClick={!this.props.onHover ? this.toggleDropdown : this.noop}
        onMouseEnter={this.props.onHover ? this.toggleDropdown : this.noop}
        onMouseLeave={this.props.onHover ? this.toggleDropdown : this.noop}
        className={listClasses}
        tabIndex='0'
        role='button'
        ref={this.makeRef}
        data-ref-name='truncateWordRef'
      >
        <span>{truncateList.first()}</span>
        {additionalCount}
      </div>
    );
  }

  makeStringContent() {
    return (
      <div
        onClick={!this.props.onHover ? this.toggleDropdown : this.noop}
        onMouseEnter={this.props.onHover ? this.toggleDropdown : this.noop}
        onMouseLeave={this.props.onHover ? this.toggleDropdown : this.noop}
        className='sg-truncate-tooltip__string'
        tabIndex='0'
        role='button'
        ref={this.makeRef}
        data-ref-name='truncateWordRef'
      >
        {this.props.content}
      </div>
    );
  }

  render() {
    const wrapperClass = classnames('sg-truncate-tooltip', this.props.className, {
      'sg-truncate-tooltip__string-wrapper': !List.isList(this.props.content)
    });
    let truncatedContent, tooltipContent;

    if (List.isList(this.props.content)) {
      truncatedContent = this.makeListContent();
      tooltipContent = this.props.content.shift().join(', ');
    } else {
      truncatedContent = this.makeStringContent();
      tooltipContent = this.props.content;
    }

    return (
      <div className={wrapperClass}>
        {truncatedContent}
        <div
          className='sg-truncate-tooltip__tooltip-wrapper'
          ref={this.makeRef}
          data-ref-name='tooltipRef'
        >
          <Tooltip
            color={this.props.color}
            position={this.props.position}
            storeName={this.storeName}
            className={this.props.tooltipClassName}
            toggleOnHover={this.props.onHover}
          >
            {tooltipContent}
          </Tooltip>
        </div>
      </div>
    );
  }
}
